/**
 * Sentry initialization for the React frontend.
 *
 * Graceful degradation: if VITE_SENTRY_DSN is not set, this is a no-op.
 * The app still runs fine without Sentry.
 *
 * Env vars:
 *   VITE_SENTRY_DSN — public DSN from sentry.io
 *   VITE_SENTRY_ENVIRONMENT — optional, defaults to import.meta.env.MODE ("development" | "production")
 */

import * as Sentry from "@sentry/react";

let initialized = false;

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  if (!dsn) {
    if (import.meta.env.PROD) {
      // In prod, missing DSN is worth noting but not fatal.
      console.warn("[sentry] VITE_SENTRY_DSN not set — error tracking disabled");
    }
    return;
  }
  if (initialized) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,

    // Performance monitoring — keep modest in v1; raise once we have real traffic
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay — useful for debugging UX failures
    // Off by default for privacy; turn on if/when needed
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    /**
     * Drop browser-extension noise before it counts against quota or fires alerts.
     *
     * The big offenders we've seen in production:
     *   - CSP `unsafe-eval` violations from crypto-wallet extensions (Backpack,
     *     MetaMask, Phantom, etc.) — they inject content scripts that try to
     *     run eval() on every page, our strict CSP correctly blocks them, but
     *     the rejection bubbles up to Sentry as an unhandled error.
     *   - ResizeObserver loop noise, "Non-Error promise rejection" — common
     *     extension/observer chatter that's never our code's fault.
     *
     * These regex strings match against the error's message; user code that
     * coincidentally throws with the same phrasing would also be dropped.
     * Unlikely in our codebase.
     */
    ignoreErrors: [
      /unsafe-eval/i,
      /Refused to evaluate a string as JavaScript/i,
      /ResizeObserver loop (limit exceeded|completed)/i,
      /Non-Error promise rejection captured/i,
    ],

    /**
     * Drop events whose stack frames come from injected extension scripts.
     * Extensions don't ship source maps, so Sentry sees them as <anonymous>
     * — the only reliable identifier is the chrome-extension:// / moz-
     * extension:// URL scheme on the originating frame.
     */
    denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-web-extension:\/\//i],

    // Scrub anything that looks like a file blob URL — files contain user data
    beforeSend(event) {
      if (event.request?.url?.startsWith("blob:")) {
        delete event.request.url;
      }
      // Don't send extraction file contents
      if (event.contexts?.["extraction"]) {
        const ctx = event.contexts["extraction"] as Record<string, unknown>;
        delete ctx.file;
        delete ctx.base64;
      }
      // Drop events whose preceding breadcrumbs are dominated by a browser
      // extension's chatter. We've specifically seen Backpack on production;
      // adding signatures as we observe them avoids over-filtering.
      const breadcrumbs = event.breadcrumbs ?? [];
      const extensionSignatures = [
        "backpack: secure-background",
        "MetaMask: chrome-extension",
        "phantom: content-script",
      ];
      const extensionHits = breadcrumbs.filter((bc) =>
        extensionSignatures.some((sig) => bc.message?.includes(sig) ?? false),
      ).length;
      if (extensionHits >= 2) return null;

      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
      // Skip Replay until we explicitly opt in — it adds bundle weight + privacy considerations
    ],
  });

  initialized = true;
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const captureException = Sentry.captureException;
